import { Alert, App as AntApp, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Table, Typography } from "antd";
import { EditOutlined, LockOutlined, PlusOutlined, UnlockOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { classKeys, defaultYear, formatDate, useAcademicYears } from "../app/classes.ts";
import {
  ASSESSMENT_NAME_MAX_LENGTH,
  gradingKeys,
  MAX_SCORE_LIMIT,
  MIN_SCORE,
  useAssessmentTypes,
  type AssessmentTypeDetails,
  type ClosedMarkingPeriod,
} from "../app/grading.ts";
import { formatSchoolDateTime } from "../app/time.ts";

const PAGE_SIZE = 25;
const NOTE_MAX_LENGTH = 500;

/**
 * Grading settings (bd-3qg.9): the assessment types with their maximum marks, and the closed marking periods. Everyone holding
 * grading.readAll sees both; only grading.manage may rename a type, change its maximum, close a period or reopen it.
 */
export function GradingSettingsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("grading.manage");

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("grading.settings.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("grading.settings.subtitle")}</Typography.Text>
      </div>
      <AssessmentTypesCard canManage={canManage} />
      <ClosedPeriodsCard canManage={canManage} />
    </Space>
  );
}

function AssessmentTypesCard({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const types = useAssessmentTypes();
  const [editing, setEditing] = useState<AssessmentTypeDetails>();

  const columns = [
    { title: t("grading.settings.typeName"), dataIndex: "name", key: "name" },
    { title: t("grading.settings.typeKind"), key: "kind", render: (_: unknown, type: AssessmentTypeDetails) => t(`grading.kinds.${type.kind}`) },
    { title: t("grading.settings.maxScore"), key: "maxScore", width: 140, render: (_: unknown, type: AssessmentTypeDetails) => Number(type.maxScore) },
    ...(canManage
      ? [
          {
            title: t("common.actions"),
            key: "actions",
            width: 120,
            render: (_: unknown, type: AssessmentTypeDetails) => (
              <Button size="small" icon={<EditOutlined />} aria-label={t("grading.settings.editType", { name: type.name })} onClick={() => setEditing(type)}>
                {t("common.edit")}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card title={t("grading.settings.typesTitle")}>
      <Typography.Paragraph type="secondary">{t("grading.settings.typesHint")}</Typography.Paragraph>
      {types.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void types.refetch()}>{t("app.retry")}</Button>} />}
      <Table<AssessmentTypeDetails> rowKey="kind" size="middle" loading={types.isPending} columns={columns} dataSource={types.data ?? []} pagination={false} scroll={{ x: "max-content" }} />
      <EditTypeModal type={editing} onClose={() => setEditing(undefined)} />
    </Card>
  );
}

interface TypeForm {
  name?: string;
  maxScore?: number;
}

function EditTypeModal({ type, onClose }: { type: AssessmentTypeDetails | undefined; onClose(): void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<TypeForm>();
  const [summary, setSummary] = useState<string[]>([]);

  useEffect(() => {
    if (!type) return;
    form.setFieldsValue({ name: type.name, maxScore: Number(type.maxScore) });
    setSummary([]);
  }, [type, form]);

  const mutation = useMutation({
    mutationFn: async (values: TypeForm) => {
      const { data, error, response } = await api.PUT("/grading/assessment-types/{kind}", {
        params: { path: { kind: type?.kind ?? "Daily" } },
        body: { name: values.name?.trim() ?? "", maxScore: values.maxScore ?? null },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gradingKeys.all });
      void message.success(t("grading.saved"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  return (
    <Modal
      open={type !== undefined}
      title={type ? t("grading.settings.editType", { name: type.name }) : ""}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon title={summary.join(" ")} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
        <Form.Item name="name" label={t("grading.settings.typeName")} rules={[{ required: true, whitespace: true, message: t("common.required") }, { max: ASSESSMENT_NAME_MAX_LENGTH }]}>
          <Input maxLength={ASSESSMENT_NAME_MAX_LENGTH} />
        </Form.Item>
        <Form.Item
          name="maxScore"
          label={t("grading.settings.maxScore")}
          rules={[{ required: true, message: t("common.required") }]}
          extra={t("grading.settings.maxScoreHint", { min: MIN_SCORE, max: MAX_SCORE_LIMIT })}
        >
          <InputNumber min={MIN_SCORE} max={MAX_SCORE_LIMIT} precision={0} style={{ width: 140 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ClosedPeriodsCard({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const { message, modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [closing, setClosing] = useState(false);

  const periods = useQuery({
    queryKey: gradingKeys.closed(page),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/closed-periods", { params: { query: { Page: page, PageSize: PAGE_SIZE } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const reopen = useMutation({
    mutationFn: async (period: ClosedMarkingPeriod) => {
      const { error, response } = await api.DELETE("/grading/closed-periods/{id}", { params: { path: { id: period.id } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gradingKeys.all });
      void message.success(t("grading.settings.reopened"));
    },
    onError: (err) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" ")),
  });

  const scope = (p: ClosedMarkingPeriod) => p.className ?? t("grading.settings.wholeSchool");
  const confirmReopen = (p: ClosedMarkingPeriod) =>
    modal.confirm({
      title: t("grading.settings.reopenConfirm", { scope: scope(p), from: formatDate(p.startDate), to: formatDate(p.endDate) }),
      okText: t("grading.settings.reopen"),
      cancelText: t("common.cancel"),
      onOk: () => reopen.mutateAsync(p).catch(() => undefined),
    });

  const columns = [
    { title: t("grading.settings.scope"), key: "scope", render: (_: unknown, p: ClosedMarkingPeriod) => scope(p) },
    { title: t("grading.settings.range"), key: "range", render: (_: unknown, p: ClosedMarkingPeriod) => `${formatDate(p.startDate)} – ${formatDate(p.endDate)}` },
    { title: t("grading.settings.note"), dataIndex: "note", key: "note", ellipsis: true },
    {
      title: t("grading.settings.closedBy"),
      key: "closedBy",
      render: (_: unknown, p: ClosedMarkingPeriod) => (
        <Space orientation="vertical" size={0}>
          <span>{p.closedByName}</span>
          <Typography.Text type="secondary">{formatSchoolDateTime(p.closedAt)}</Typography.Text>
        </Space>
      ),
    },
    ...(canManage
      ? [
          {
            title: t("common.actions"),
            key: "actions",
            width: 150,
            render: (_: unknown, p: ClosedMarkingPeriod) => (
              <Button size="small" icon={<UnlockOutlined />} aria-label={t("grading.settings.reopenNamed", { scope: scope(p) })} onClick={() => confirmReopen(p)}>
                {t("grading.settings.reopen")}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      title={t("grading.settings.periodsTitle")}
      extra={canManage && <Button type="primary" icon={<PlusOutlined />} onClick={() => setClosing(true)}>{t("grading.settings.close")}</Button>}
    >
      <Typography.Paragraph type="secondary">{t("grading.settings.periodsHint")}</Typography.Paragraph>
      {periods.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void periods.refetch()}>{t("app.retry")}</Button>} />}
      <Table<ClosedMarkingPeriod>
        rowKey="id"
        size="middle"
        loading={periods.isPending}
        columns={columns}
        dataSource={periods.data?.items ?? []}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: Number(periods.data?.totalCount ?? 0), onChange: setPage, hideOnSinglePage: true }}
        locale={{ emptyText: t("grading.settings.noPeriods") }}
        scroll={{ x: "max-content" }}
      />
      <ClosePeriodModal open={closing} onClose={() => setClosing(false)} />
    </Card>
  );
}

interface CloseForm {
  classId?: string;
  range?: [Dayjs, Dayjs];
  note?: string;
}

const WHOLE_SCHOOL = "";

function ClosePeriodModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CloseForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const years = useAcademicYears();
  const year = defaultYear(years.data);
  const classes = useQuery({
    queryKey: classKeys.list(year?.id),
    enabled: open && year !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes", { params: { query: year ? { AcademicYearId: year.id } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({ classId: WHOLE_SCHOOL });
    setSummary([]);
  }, [open, form]);

  const mutation = useMutation({
    mutationFn: async (values: CloseForm) => {
      const [start, end] = values.range ?? [];
      const { data, error, response } = await api.POST("/grading/closed-periods", {
        body: {
          classId: values.classId ? values.classId : null,
          startDate: start ? start.format("YYYY-MM-DD") : null,
          endDate: end ? end.format("YYYY-MM-DD") : null,
          note: values.note?.trim() || null,
        },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gradingKeys.all });
      void message.success(t("grading.settings.closed"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const classOptions = [
    { value: WHOLE_SCHOOL, label: t("grading.settings.wholeSchool") },
    ...(classes.data ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <Modal
      open={open}
      title={t("grading.settings.close")}
      okText={t("grading.settings.closeAction")}
      okButtonProps={{ icon: <LockOutlined /> }}
      cancelText={t("common.cancel")}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Alert type="warning" showIcon title={t("grading.settings.closeWarning")} style={{ marginBottom: 16 }} />
      {summary.length > 0 && <Alert type="error" showIcon title={summary.join(" ")} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
        <Form.Item name="classId" label={t("grading.settings.scope")}>
          <Select options={classOptions} loading={classes.isPending && classes.fetchStatus !== "idle"} showSearch={{ optionFilterProp: "label" }} />
        </Form.Item>
        <Form.Item name="range" label={t("grading.settings.range")} rules={[{ required: true, message: t("grading.settings.rangeRequired") }]}>
          <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="note" label={t("grading.settings.note")} rules={[{ max: NOTE_MAX_LENGTH }]}>
          <Input.TextArea rows={2} maxLength={NOTE_MAX_LENGTH} placeholder={t("grading.settings.notePlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
